import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import NumaMascot from "@/components/NumaMascot";
import { auth, normalizeError } from "@/lib/api";

/**
 * La pagina che apre il link arrivato per posta.
 *
 * Non fa accedere: sceglie la password e basta, e poi rimanda al modulo di
 * accesso. Un link che entrasse nell'account sarebbe una sessione lasciata in
 * una casella di posta - e le caselle si condividono, si dimenticano aperte, si
 * inoltrano per sbaglio.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [conferma, setConferma] = useState("");
  const [loading, setLoading] = useState(false);
  const [fatto, setFatto] = useState(false);
  const [problem, setProblem] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setProblem(null);

    // Le stesse regole del server, dette prima del viaggio invece che dopo.
    if (password.length < 10) {
      setProblem({ message: "La password deve avere almeno 10 caratteri." });
      return;
    }
    if (/^[a-zA-Z]+$/.test(password) || /^[0-9]+$/.test(password)) {
      setProblem({ message: "Aggiungi almeno un numero o un simbolo." });
      return;
    }
    if (password !== conferma) {
      setProblem({ message: "Le due password non coincidono." });
      return;
    }

    setLoading(true);
    try {
      await auth.resetPassword(token, password);
      setFatto(true);
    } catch (err) {
      const e = normalizeError(err);
      setProblem({ message: e.message || "Non è stato possibile cambiare la password.", action: e.action });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 py-12" data-testid="reset-page">
      <div className="w-full max-w-[380px]">
        <div className="mb-8 flex items-center gap-2.5">
          <NumaMascot size={30} className="numa-mark" variant={0} />
          <span className="font-serif-display text-lg font-semibold text-ink">NUMA</span>
        </div>

        {!token ? (
          <>
            <h1 className="font-serif-display text-2xl font-semibold text-ink">Link incompleto</h1>
            <p className="numa-meta mt-2 leading-relaxed">
              L&apos;indirizzo non contiene il codice: copialo dalla mail per intero, oppure
              richiedine un altro dalla pagina di accesso.
            </p>
            <Button className="mt-6 h-10 w-full" onClick={() => navigate("/login")}>
              Torna all&apos;accesso
            </Button>
          </>
        ) : fatto ? (
          <>
            <h1 className="flex items-center gap-2 font-serif-display text-2xl font-semibold text-ink">
              <CheckCircle2 className="h-5 w-5 text-ok" /> Password cambiata
            </h1>
            <p className="numa-meta mt-2 leading-relaxed">
              Da ora entri con quella nuova. Il link che hai usato non funziona più.
            </p>
            <Button
              data-testid="reset-go-login"
              className="mt-6 h-10 w-full"
              onClick={() => navigate("/login", { replace: true })}
            >
              Vai all&apos;accesso
            </Button>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-2xl font-semibold text-ink">
              Scegli una nuova password
            </h1>
            <p className="numa-meta mt-1">Almeno 10 caratteri, con una cifra o un simbolo.</p>

            {problem && (
              <div role="alert" data-testid="reset-error"
                   className="mt-6 rounded-md border border-danger/40 bg-danger/5 px-3.5 py-3">
                <p className="flex items-start gap-2 text-[13px] font-medium text-ink">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
                  {problem.message}
                </p>
                {problem.action && <p className="numa-meta mt-1 pl-[22px]">{problem.action}</p>}
              </div>
            )}

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="nuova">Nuova password</Label>
                <PasswordInput
                  id="nuova"
                  data-testid="reset-password-input"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="conferma">Ripetila</Label>
                <PasswordInput
                  id="conferma"
                  data-testid="reset-confirm-input"
                  autoComplete="new-password"
                  required
                  value={conferma}
                  onChange={(e) => setConferma(e.target.value)}
                  className="h-10"
                />
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={loading} className="h-10 w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Salva la password
              </Button>
            </form>

            <p className="mt-6 text-[13px] text-ink-muted">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-medium text-brand hover:underline"
              >
                Torna all&apos;accesso
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
